import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContextEDH } from "@context/ContextEDH";
import { GroupContext } from "../context/GroupContext";
import {
  EnrollmentRestriction,
  EnrollmentRestrictionOperator,
  EnrollmentRestrictionValueUnion,
  Subject,
} from "@models/ISchema";
import { PROPERTY_TYPES } from "./useValueSelectorFactory";

export interface AdaptedEnrollmentRestriction extends EnrollmentRestriction {
  ogOperator?: EnrollmentRestrictionOperator;
}

export type AdaptedEnrollmentRestrictionValue = EnrollmentRestrictionValueUnion & {
  self?: any;
};

const PROPERTY_LABELS = {
  [PROPERTY_TYPES["modality"].key]: PROPERTY_TYPES["modality"].nameI18n,
  [PROPERTY_TYPES["program"].key]: PROPERTY_TYPES["program"].nameI18n,
  [PROPERTY_TYPES["curriculum"].key]: PROPERTY_TYPES["curriculum"].nameI18n,
  [PROPERTY_TYPES["class"].key]: PROPERTY_TYPES["class"].nameI18n,
  [PROPERTY_TYPES["student_attributes"].key]: PROPERTY_TYPES["student_attributes"].nameI18n,
  [PROPERTY_TYPES["cohorts"].key]: PROPERTY_TYPES["cohorts"].nameI18n,
};

const useGroupRestrictionsValues = () => {
  const { state } = useContext(ContextEDH);
  const { state: groupInfoState } = useContext(GroupContext);
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [groupSubject, setGroupSubject] = useState<Subject | null>(null);
  const [groupEnrollmentRestrictions, setGroupEnrollmentRestrictions] = useState<
    AdaptedEnrollmentRestriction[] | null
  >(null);

  useEffect(() => {
    setGroupSubject(
      groupInfoState?.info?.info?.subject
        ? structuredClone(groupInfoState?.info?.info?.subject)
        : null,
    );

    const adaptedGroupEnrollmentRestrictions = groupInfoState?.info?.info?.groupEnrollmentRestrictions?.map(
      restriction => {
        return {
          ...restriction,
          ogOperator: restriction?.operator,
        };
      },
    );

    setGroupEnrollmentRestrictions(
      groupInfoState?.info?.info?.groupEnrollmentRestrictions
        ? structuredClone(adaptedGroupEnrollmentRestrictions)
        : null,
    );
  }, [groupInfoState]);

  const ogGroupEnrollmentRestrictionsByAttribute = useMemo(() => {
    return groupInfoState?.info?.info?.groupEnrollmentRestrictions?.reduce((acc, restriction) => {
      acc[restriction.attribute] = restriction;
      return acc;
    }, {});
  }, [groupInfoState?.info?.info?.groupEnrollmentRestrictions]);

  const { restrictionsByKey, groupsRestrictionProperties } = useMemo(() => {
    const restrictionsByKey = new Map();
    let groupsRestrictionProperties = [];

    if (!!state?.base?.restrictionConfigurations?.enabled) {
      groupsRestrictionProperties =
        state?.base?.restrictionConfigurations?.fields?.groups?.map(group => {
          const labelKey = PROPERTY_LABELS[group?.key];

          const adaptedValue = {
            label: labelKey ? t(labelKey) : group?.name,
            value: group?.key,
            self: group,
          };

          restrictionsByKey.set(group?.key, adaptedValue);

          return adaptedValue;
        }) || [];
    }

    return {
      restrictionsByKey,
      groupsRestrictionProperties,
    };
  }, [state?.base?.restrictionConfigurations?.fields?.groups, language]);

  const inheritedSubjectRestrictions = useMemo(() => {
    const subjectRestrictions = groupSubject?.enrollmentRestrictions ?? [];
    const subjectRestrictionsByKey = {};

    const restrictions = subjectRestrictions.map(restriction => {
      const value = {
        label: restrictionsByKey.get(restriction.attribute)?.label ?? restriction?.attribute,
        value: restriction.attribute,
        operatorLabel: t(
          `group-restrictions.manage-restrictions.form.conditions.${restriction.operator}`,
          {
            defaultValue: restriction.operator,
          },
        ),
        isInherited: true,
        self: restriction,
      };

      subjectRestrictionsByKey[restriction.attribute] = value;

      return value;
    });

    return {
      restrictions,
      subjectRestrictionsByKey,
    };
  }, [restrictionsByKey, groupSubject, language]);

  const groupRestrictions = useMemo(() => {
    const restrictions = groupEnrollmentRestrictions ?? [];
    const groupRestrictionsByKey = {};

    const adaptedRestrictions = restrictions.map(restriction => {
      const value = {
        label: restrictionsByKey?.get(restriction.attribute)?.label ?? restriction?.attribute,
        value: restriction.attribute,
        ogOperatorLabel: restriction.ogOperator
          ? t(`group-restrictions.manage-restrictions.form.conditions.${restriction.ogOperator}`, {
              defaultValue: restriction.ogOperator,
            })
          : null,
        operatorLabel: t(
          `group-restrictions.manage-restrictions.form.conditions.${restriction.operator}`,
          {
            defaultValue: restriction.operator,
          },
        ),
        self: restriction,
      };

      groupRestrictionsByKey[restriction.attribute] = value;

      return value;
    });

    return {
      restrictions: adaptedRestrictions,
      groupRestrictionsByKey,
    };
  }, [restrictionsByKey, groupEnrollmentRestrictions, language]);

  const selectedRestrictionsByPropertyKey = useMemo(() => {
    const selectedRestrictionsByProperty = {};

    [
      ...(inheritedSubjectRestrictions?.restrictions ?? []),
      ...(groupRestrictions?.restrictions ?? []),
    ].forEach(rest => {
      const { isInherited, ...restriction } = rest as any;

      const values = restriction?.self?.values ?? [];
      const adaptedValues = !isInherited
        ? values
        : values?.map(value => ({
            ...value,
            isInherited: true,
          }));

      if (restriction?.value in selectedRestrictionsByProperty) {
        selectedRestrictionsByProperty[restriction?.value]?.valueIds?.push(
          ...(restriction?.self?.valueIds ?? []),
        );

        selectedRestrictionsByProperty[restriction?.value]?.values?.push(...adaptedValues);
      } else {
        selectedRestrictionsByProperty[restriction?.value] = {
          restriction,
          valueIds: restriction?.self?.valueIds ?? [],
          values: adaptedValues,
        };
      }
    });

    return selectedRestrictionsByProperty;
  }, [inheritedSubjectRestrictions?.restrictions, groupRestrictions?.restrictions]);

  const groupEnrollmentRestrictionsHasChanged = useMemo(() => {
    if (
      Object.keys(ogGroupEnrollmentRestrictionsByAttribute ?? {}).length !==
      groupEnrollmentRestrictions?.length
    ) {
      return true;
    }

    return groupEnrollmentRestrictions?.some(restriction => {
      const ogRestriction = ogGroupEnrollmentRestrictionsByAttribute[restriction.attribute];

      if (!ogRestriction || (ogRestriction && ogRestriction?.operator !== restriction.operator)) {
        return true;
      }

      const hasDifferentValueIds =
        ogRestriction?.valueIds?.length !== restriction.valueIds?.length ||
        ogRestriction?.valueIds?.some(valueId => !restriction.valueIds?.includes(valueId));

      if (hasDifferentValueIds) {
        return true;
      }

      return false;
    });
  }, [ogGroupEnrollmentRestrictionsByAttribute, groupEnrollmentRestrictions]);

  const handleAddGroupRestrictionValue = (payload: {
    attribute: string;
    operator: EnrollmentRestrictionOperator;
    table?: string;
    value: {
      id: number | string;
      code: string;
      name: string;
    };
  }) => {
    const clonedGroupRestrictions = [...(groupEnrollmentRestrictions ?? [])];
    const currentRestrictionIndex = clonedGroupRestrictions.findIndex(
      restriction => restriction?.attribute === payload?.attribute,
    );

    if (currentRestrictionIndex !== -1) {
      clonedGroupRestrictions[currentRestrictionIndex] = {
        ...clonedGroupRestrictions[currentRestrictionIndex],
        operator: payload?.operator,
        valueIds: [
          ...(clonedGroupRestrictions[currentRestrictionIndex]?.valueIds ?? []),
          +payload?.value?.id,
        ],
        values: [
          ...(clonedGroupRestrictions[currentRestrictionIndex]?.values ?? []),
          {
            id: payload?.value?.id.toString(),
            code: payload?.value?.code,
            name: payload?.value?.name,
            self: payload?.value,
          } as AdaptedEnrollmentRestrictionValue,
        ],
      };
    } else {
      clonedGroupRestrictions.push({
        attribute: payload?.attribute,
        operator: payload?.operator,
        table: payload?.table ?? "",
        valueIds: [+payload?.value?.id],
        values: [
          {
            id: payload?.value?.id.toString(),
            code: payload?.value?.code,
            name: payload?.value?.name,
            self: payload?.value,
          } as AdaptedEnrollmentRestrictionValue,
        ],
      });
    }

    setGroupEnrollmentRestrictions(clonedGroupRestrictions);
  };

  const handleRemoveGroupRestriction = (payload: { attribute: string }) => {
    const clonedGroupRestrictions = [...(groupEnrollmentRestrictions ?? [])];
    const currentRestrictionIndex = clonedGroupRestrictions.findIndex(
      restriction => restriction?.attribute === payload?.attribute,
    );

    if (currentRestrictionIndex !== -1) {
      clonedGroupRestrictions.splice(currentRestrictionIndex, 1);
    }

    setGroupEnrollmentRestrictions(clonedGroupRestrictions);
  };

  const handleRemoveGroupRestrictionValue = (payload: {
    attribute: string;
    valueId: number | string;
  }) => {
    const clonedGroupRestrictions = [...(groupEnrollmentRestrictions ?? [])];
    const currentRestrictionIndex = clonedGroupRestrictions.findIndex(
      restriction => restriction?.attribute === payload?.attribute,
    );
    const currentRestrictionValueIdsIndex = clonedGroupRestrictions[
      currentRestrictionIndex
    ]?.valueIds?.findIndex(valueId => valueId === payload?.valueId);
    const currentRestrictionValuesIndex = clonedGroupRestrictions[
      currentRestrictionIndex
    ]?.values?.findIndex(value => value?.id === payload?.valueId);

    if (clonedGroupRestrictions[currentRestrictionIndex]?.valueIds?.length === 1) {
      handleRemoveGroupRestriction({
        attribute: payload?.attribute,
      });

      return;
    }

    clonedGroupRestrictions[currentRestrictionIndex]?.valueIds?.splice(
      currentRestrictionValueIdsIndex,
      1,
    );
    clonedGroupRestrictions[currentRestrictionIndex]?.values?.splice(
      currentRestrictionValuesIndex,
      1,
    );

    setGroupEnrollmentRestrictions(clonedGroupRestrictions);
  };

  const handleUpdateGroupRestrictionOperator = (payload: {
    attribute: string;
    operator: EnrollmentRestrictionOperator;
  }) => {
    const clonedGroupRestrictions = [...(groupEnrollmentRestrictions ?? [])];
    const currentRestrictionIndex = clonedGroupRestrictions.findIndex(
      restriction => restriction?.attribute === payload?.attribute,
    );

    if (currentRestrictionIndex !== -1) {
      clonedGroupRestrictions[currentRestrictionIndex] = {
        ...clonedGroupRestrictions[currentRestrictionIndex],
        operator: payload?.operator,
      };

      setGroupEnrollmentRestrictions(clonedGroupRestrictions);
    }
  };

  const handleResetStates = () => {
    setGroupSubject(null);
    setGroupEnrollmentRestrictions(null);
  };

  return {
    groupsRestrictionProperties,
    groupRestrictions: groupRestrictions?.restrictions ?? [],
    groupRestrictionsByKey: groupRestrictions?.groupRestrictionsByKey ?? {},
    inheritedSubjectRestrictions: inheritedSubjectRestrictions?.restrictions ?? [],
    inheritedSubjectRestrictionsByKey: inheritedSubjectRestrictions?.subjectRestrictionsByKey ?? {},
    restrictionsByKey,
    selectedRestrictionsByPropertyKey,
    groupEnrollmentRestrictionsHasChanged,
    isEmpty: !groupInfoState?.info?.info,
    handleAddGroupRestrictionValue,
    handleRemoveGroupRestrictionValue,
    handleRemoveGroupRestriction,
    handleUpdateGroupRestrictionOperator,
    handleResetStates,
  };
};

export default useGroupRestrictionsValues;
